import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  MenuItem,
  InputAdornment,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  createCompanyUser,
  updateCompanyUser,
  fetchCompanyUsers,
} from "./companyUsersSlice";

const fieldSx = {
  "& .MuiInputBase-input": { fontSize: "0.875rem" },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "background.paper",
  },
};

const usernameRegex = /^[a-z0-9._-]+$/;

const ROLE_OPTIONS = [
  { value: "1", label: "Standard", description: "Regular access" },
  { value: "5", label: "Admin", description: "Full management access" },
  { value: "6", label: "Operator", description: "Operational tasks only" },
];

const buildSchema = (isEdit) =>
  Yup.object({
    name: Yup.string().trim().required("Required").max(80),
    username: Yup.string()
      .trim()
      .lowercase()
      .min(3, "Min 3 characters")
      .max(40)
      .matches(usernameRegex, "Letters, numbers, dot, underscore or dash only")
      .required("Required"),
    password: isEdit
      ? Yup.string().min(4, "Min 4 characters")
      : Yup.string().min(4, "Min 4 characters").required("Required"),
    role: Yup.string().trim().required("Required"),
  });

const emptyValues = {
  name: "",
  username: "",
  password: "",
  role: "1",
};

const initialFromUser = (u) => ({
  name: u?.name || "",
  username: u?.username || "",
  password: "",
  role: u?.role ? String(u.role) : "1",
});

export default function UserFormDialog({ open, onClose, companyId, user }) {
  const dispatch = useDispatch();
  const { saving, query } = useSelector((s) => s.companyUsers);
  const isEdit = Boolean(user);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: user ? initialFromUser(user) : emptyValues,
    validationSchema: buildSchema(isEdit),
    onSubmit: async (values) => {
      const payload = { ...values };
      if (isEdit && !payload.password) delete payload.password;

      const action = isEdit
        ? updateCompanyUser({ id: user._id, payload })
        : createCompanyUser({ companyId, payload });
      const res = await dispatch(action);
      const ok = isEdit
        ? updateCompanyUser.fulfilled.match(res)
        : createCompanyUser.fulfilled.match(res);

      if (ok) {
        toast.success(isEdit ? "User updated" : "User created");
        onClose();
        dispatch(fetchCompanyUsers({ companyId, params: query }));
      } else {
        toast.error(res.payload || "Save failed");
      }
    },
  });

  useEffect(() => {
    if (!open) formik.resetForm();
    // eslint-disable-next-line
  }, [open]);

  const initials = (user?.name || formik.values.name || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join("") || "U";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          mx: 2,
          width: { xs: "calc(100% - 32px)", sm: "100%" },
          overflow: "hidden",
        },
      }}
    >
      {/* Gradient header band — gives the dialog a polished, branded feel */}
      <Box
        sx={{
          position: "relative",
          px: 2,
          pt: 2.5,
          pb: 2,
          background:
            "linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)",
          color: "common.white",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "common.white",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: "rgba(255,255,255,0.22)",
              color: "common.white",
              fontSize: "1.0625rem",
              fontWeight: 700,
              border: "2px solid rgba(255,255,255,0.35)",
            }}
          >
            {isEdit ? initials : <PersonIcon />}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              {isEdit ? (
                <EditIcon sx={{ fontSize: 16 }} />
              ) : (
                <PersonIcon sx={{ fontSize: 16 }} />
              )}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.15,
                }}
              >
                {isEdit ? "Edit User" : "New User"}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: "0.75rem",
                opacity: 0.92,
                mt: 0.25,
                wordBreak: "break-word",
              }}
            >
              {isEdit
                ? `${user.name} - ${user.username}`
                : "Create a user that belongs to this company"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <form id="user-form" onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ p: 2, backgroundColor: "background.default" }}>
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Full Name *"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              placeholder="Full name"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Username *"
              name="username"
              value={formik.values.username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
              placeholder="Username"
              sx={fieldSx}
              inputProps={{ "data-no-uppercase": "true" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              size="small"
              type="password"
              label={isEdit ? "New Password" : "Password *"}
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete="new-password"
              placeholder={isEdit ? "Leave blank to keep current" : "Min 4 characters"}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={
                (formik.touched.password && formik.errors.password) ||
                (isEdit ? "Only enter to change the existing password" : "")
              }
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              size="small"
              select
              label="Role *"
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.role && Boolean(formik.errors.role)}
              helperText={formik.touched.role && formik.errors.role}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AdminPanelSettingsIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
            >
              {ROLE_OPTIONS.map((r) => (
                <MenuItem
                  key={r.value}
                  value={r.value}
                  sx={{ fontSize: "0.8125rem", py: 0.75 }}
                >
                  <Stack>
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                      {r.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.6875rem", color: "text.secondary" }}
                    >
                      {r.description}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ borderRadius: "8px", flex: { xs: 1, sm: "0 0 auto" } }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{
              borderRadius: "8px",
              flex: { xs: 1, sm: "0 0 auto" },
              boxShadow: "0 4px 10px -4px rgba(13,148,136,0.55)",
            }}
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create User"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
